import React from 'react';
import { Button } from 'antd';
import { ArrowRightOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderStatus } from '../supplierOrderStatuses';
import { getSupplierOrderStageIndex } from './supplierOrderDraftStage';
import {
  type SupplierOrderStageActionKey,
  type SupplierOrderNextStatus,
  type SupplierOrderStageModel,
  type SupplierOrderStageTask,
} from './supplierOrderStageModel';
import SupplierOrderStageRail from './SupplierOrderStageRail';
import './SupplierOrderDraftStage.css';

const DONE_VARIANT = new Set<string>(['transport', 'pz']);

interface SupplierOrderStagePanelProps {
  status: SupplierOrderStatus;
  model: SupplierOrderStageModel;
  canManage: boolean;
  /** Statuses the backend lets this ZD move to (informational statuses, any direction). */
  allowedTransitions: string[];
  /** Number of trucks (text of the transport task). */
  transportCount: number | null;
  /** Task actions this viewer may not use (planning trucks, PZ, purchase invoices have their own roles). */
  unavailableActions?: SupplierOrderStageActionKey[];
  saving?: boolean;
  onAction: (action: SupplierOrderStageActionKey) => void;
  onNextStatus: (status: SupplierOrderNextStatus) => void;
  /** Click on a finished stage of the rail: go back to it (the card confirms and sets the status). */
  onReturnToStage: (stageIndex: number) => void;
  /** Side card „Dostawa” (trucks, freight, invoice) next to the step card. */
  aside?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Stages 2–5 of the ZD card (makieta `renderRail`, `renderStep`): the rail with
 * the detailed status under the current stage, „Etap n z 5 · status: …”, what
 * to do, the task list with each task's own action and the one next-status
 * button. Stage 1 „Szkic” is `SupplierOrderDraftStagePanel`; both use the
 * same classes and test ids, so a ZD looks like one card that differs by stage.
 */
export default function SupplierOrderStagePanel({
  status,
  model,
  canManage,
  allowedTransitions,
  transportCount,
  unavailableActions = [],
  saving = false,
  onAction,
  onNextStatus,
  onReturnToStage,
  aside,
  children,
}: SupplierOrderStagePanelProps) {
  const { t } = useTranslation(['orders']);
  const stageIndex = Math.max(getSupplierOrderStageIndex(status), 0);
  const stageKey = ['draft', 'ordered', 'onTheWay', 'received', 'completed'][stageIndex];
  const statusLabel = t(`orders:supplierOrders.status.${status}`);
  const usable = (action: SupplierOrderStageActionKey | undefined | null) => Boolean(action) && canManage && !unavailableActions.includes(action as SupplierOrderStageActionKey);
  const primary = usable(model.primaryAction) ? model.primaryAction : null;
  const next = canManage && model.nextStatus && allowedTransitions.includes(model.nextStatus) ? model.nextStatus : null;
  const nextDisabled = model.blocking.length > 0;

  const taskText = (task: SupplierOrderStageTask): { text: string; hint?: string } => {
    const base = `orders:supplierOrders.card.stageTasks.${task.key}`;
    const values = { ...(task.values || {}), count: transportCount ?? 0 };
    // „Transport zaplanowany” and „PZ” read differently once done; the rest has one text.
    const text = t(task.done && DONE_VARIANT.has(task.key) ? `${base}Done` : base, values);
    return { text, hint: task.done ? undefined : t(`${base}Hint`, { defaultValue: '' }) || undefined };
  };

  return (
    <section className="zd-stage" data-testid="supplier-order-stage-panel" aria-label={t('orders:supplierOrders.card.ariaLabel')}>
      <SupplierOrderStageRail
        currentIndex={stageIndex}
        subStatusLabel={statusLabel}
        // „Zakończone” is set only by the purchase invoice: going back from it could not be undone by hand.
        onReturnToStage={canManage && status !== 'INVOICED' ? onReturnToStage : undefined}
      />

      <div className={`zd-stage__grid${aside ? ' has-aside' : ''}`}>
        <div className="zd-stage__body">
          <div className="zd-stage__eyebrow" data-testid="supplier-order-stage-eyebrow">
            {t('orders:supplierOrders.card.stageOf', { current: stageIndex + 1, total: 5 })}
            {` · ${t('orders:supplierOrders.card.statusLine', { status: statusLabel })}`}
          </div>
          <h3 className="zd-stage__title">{t(`orders:supplierOrders.card.stages.${stageKey}`)}</h3>
          <p className="zd-stage__help">{t(model.helpKey)}</p>

          <ul className="zd-stage__checks" data-testid="supplier-order-stage-checklist">
            {model.tasks.map((task) => {
              const { text, hint } = taskText(task);
              const state = task.info ? 'info' : task.done ? 'ok' : 'open';
              const showAction = usable(task.action) && task.action !== primary && (!task.done || task.info);
              return (
                <li key={task.key} className={`zd-stage__check is-${state}`} data-testid={`supplier-order-task-${task.key}`} data-state={state}>
                  <span className="zd-stage__check-icon" aria-hidden="true">
                    {task.info ? <InfoCircleOutlined /> : task.done ? <CheckOutlined /> : null}
                  </span>
                  <span className="zd-stage__check-text">
                    <span>{text}</span>
                    {hint ? <small>{hint}</small> : null}
                  </span>
                  {showAction && task.action ? (
                    <Button size="small" onClick={() => onAction(task.action as SupplierOrderStageActionKey)}>
                      {t(`orders:supplierOrders.card.stageActions.${task.action}`)}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {primary || next ? (
            <div className="zd-stage__actions">
              {primary ? (
                <Button
                  type={next ? 'default' : 'primary'}
                  onClick={() => onAction(primary)}
                  data-testid="supplier-order-stage-primary"
                >
                  {t(`orders:supplierOrders.card.stageActions.${primary}`)}
                </Button>
              ) : null}
              {next ? (
                <Button
                  type="primary"
                  disabled={nextDisabled}
                  loading={saving}
                  onClick={() => onNextStatus(next)}
                  icon={<ArrowRightOutlined />}
                  iconPosition="end"
                  data-testid="supplier-order-stage-next"
                >
                  {t(`orders:supplierOrders.card.next.${next}`)}
                </Button>
              ) : null}
              {next && nextDisabled ? (
                <span className="zd-stage__blocked" data-testid="supplier-order-stage-next-blocked">
                  {t('orders:supplierOrders.card.nextBlocked')}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        {aside ? <aside className="zd-stage__aside" data-testid="supplier-order-stage-aside">{aside}</aside> : null}
      </div>

      {children ? <div className="zd-stage__extra">{children}</div> : null}
    </section>
  );
}
